import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Common/Sidebar";
import Header from "../Common/Header";
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import Summary from "../Questionnaire/Summary";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function HistoryLandingPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const exportingPdfRef = useRef(false);

  // pagination starting index for sequence column
  const [first, setFirst] = useState(0);
  const [exportingPdf, setExportingPdf] = useState(false); // show spinner while generating PDF
  useEffect(() => {
    getHistoryList();
  }, []);
  const onSidebarClick = (val: any) => {
    setSidebarOpen(val);
  };
  const getHistoryList = () => {
    setLoading(true);
    var token = localStorage.getItem("igtoken");
    var config = {
      method: "get",
      url: `${process.env.REACT_APP_MAPSAPI}/api/account/historylist`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    axios(config)
      .then((response: any) => {
        setLoading(false);
        // Normalize and enrich data with sortable fields
        const enriched = Array.isArray(response.data)
          ? response.data.map((item: any) => ({
              ...item,
              created: item.created,
              status: item.completedDate ? "Completed" : "Incomplete",
            }))
          : [];
        // Sort by created date descending (most recent first)
        const sorted = enriched.sort((a: any, b: any) => {
          const aTime = a?.created ? new Date(a.created).getTime() : 0;
          const bTime = b?.created ? new Date(b.created).getTime() : 0;
          return bTime - aTime; // descending
        });
        setHistoryList(sorted);
        setFilteredHistory(sorted);
      })
      .catch((error: any) => {
        setLoading(false);
      });
  };

  // Apply search filtering whenever search term or original list changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredHistory(historyList);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredHistory(
      historyList.filter((item) => {
        const agency = (item.agencyName || "").toLowerCase();
        const user = (item.userName || "").toLowerCase();
        const email = (item.workEmail || "").toLowerCase();
        return agency.includes(term) || user.includes(term) || email.includes(term);
      })
    );
  }, [searchTerm, historyList]);

  const exportToPDF = async () => {
    if (exportingPdfRef.current || exportingPdf) return; // prevent double clicks
    exportingPdfRef.current = true;
    setExportingPdf(true); // show spinner in button

    try {
      const input = document.getElementById("summary-content");
      if (!input) return;

      const scheduleBtn = input.querySelector(".schedule-btn") as HTMLElement | null;
      if (scheduleBtn) scheduleBtn.style.display = "none";

      const originalOverflow = (input as HTMLElement).style.overflow;
      (input as HTMLElement).style.overflow = "visible";

      await new Promise((resolve) => setTimeout(resolve, 200));

      const fullWidth = input.scrollWidth;
      const fullHeight = input.scrollHeight;

      // Capture at high quality
      const canvas = await html2canvas(input, {
        scale: 1.1,
        useCORS: true,
        scrollY: 0,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        backgroundColor: "#ffffff",
      });

      if (scheduleBtn) scheduleBtn.style.display = "";
      (input as HTMLElement).style.overflow = originalOverflow;

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth(); // 210 mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297 mm

      // Calculate image dimensions (in px)
      const imgPxWidth = canvas.width;
      const imgPxHeight = canvas.height;

      // jsPDF uses mm units, convert pixel ratio
      const pxToMm = pageWidth / imgPxWidth;

      // Fit width to A4 exactly (no zoom)
      const imgWidth = pageWidth;
      const imgHeight = imgPxHeight * pxToMm;

      let heightLeft = imgHeight;
      let position = 0;

      const pageHeightMm = pageHeight;

      while (heightLeft > 0) {
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeightMm;
        if (heightLeft > 0) {
          pdf.addPage();
          position -= pageHeightMm;
        }
      }

      pdf.save(`QuestionnaireSummary_${selectedItem?.transactionId || "export"}.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
    } finally {
      exportingPdfRef.current = false;
      setExportingPdf(false); // hide spinner
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar pagename="history" onSidebarClick={() => onSidebarClick(false)} sidebarOpen={sidebarOpen} />
      {/* Main Content */}
      <main className="flex-1 h-screen overflow-y-auto p-4 md:p-8">
        <Header onSidebarClick={() => onSidebarClick(true)} />
        <div className="flex flex-col relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
              <ProgressSpinner strokeWidth="4" style={{ width: "50px", height: "50px" }} />
            </div>
          )}
          {/* Search Bar with inline clear icon */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Search by agency or agent name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border rounded px-3 py-2 pr-10 focus:ring-2 focus:ring-green-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                >
                  <i className="pi pi-times" />
                </button>
              )}
            </div>
          </div>
          <DataTable
            value={filteredHistory}
            sortField="created"
            sortOrder={-1}
            paginator
            paginatorTemplate="PrevPageLink PageLinks NextPageLink"
            rows={10}
            first={first}
            onPage={(e) => setFirst(e.first)}
            className="momentum-table"
            rowClassName={(data, options: any) => (options.rowIndex % 2 === 0 ? "row-even" : "row-odd")}
            emptyMessage={<div className="py-8 text-center text-gray-500">No available options</div>}
          >
            <Column
              header="Agent Name"
              field="userName"
              sortable
              body={(rowData) => {
                const name: string = rowData.userName || "";
                const parts = name
                  .trim()
                  .split(/\s+/)
                  .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
                return parts.join(" ");
              }}
            />
            <Column header="Agency Name" field="agencyName" sortable />
            <Column header="Work Email" field="workEmail" sortable />
            <Column header="Diagnostic Score" field="diagnosticScore" sortable body={(rowData) => Number(rowData.diagnosticScore) || 0} />
            <Column
              header="Created Date"
              field="created"
              sortable
              body={(rowData) => {
                if (!rowData.created) return "";
                const d = new Date(rowData.created);
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const dd = String(d.getDate()).padStart(2, "0");
                const yyyy = d.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
              }}
            />
            <Column
              header="Status"
              field="status"
              sortable
              body={(rowData) =>
                rowData.completedDate ? (
                  <span className="text-success fw-semibold">Completed</span>
                ) : (
                  <span className="text-danger fw-semibold">Incomplete</span>
                )
              }
            />
            <Column
              header="Action"
              body={(rowData) => (
                <div className="flex items-center gap-2 action-cell">
                  {rowData.completedDate && (
                    <i
                      className="pi pi-eye text-primary"
                      title="View Questionnaire Details"
                      onClick={() => {
                        setSelectedItem(rowData);
                        setShowDetails(true);
                      }}
                    />
                  )}
                </div>
              )}
            />
          </DataTable>
          <Dialog
            visible={showDetails}
            modal
            draggable={false}
            style={{ width: "90vw", maxWidth: "1200px" }}
            onHide={() => setShowDetails(false)}
            header={
              <div className="flex items-center justify-between w-full">
                <span className="text-lg font-semibold text-gray-800">Questionnaire Summary</span>

                <button
                  onClick={() => exportToPDF()}
                  title={exportingPdf ? "Exporting..." : "Export to PDF"}
                  className={`flex items-center gap-2 text-red-500 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  disabled={exportingPdf}
                >
                  {exportingPdf ? (
                    <>
                      <i className="pi pi-spin pi-spinner text-xl" />
                      <span className="text-sm font-medium">Generating PDF...</span>
                    </>
                  ) : (
                    <>
                      <i className="pi pi-file-pdf text-xl" />
                    </>
                  )}
                </button>
              </div>
            }
          >
            {selectedItem ? (
              <div>
                <div id="summary-content" className="p-3 bg-white rounded shadow-sm">
                  <Summary
                    embedded
                    suppressPost
                    disableEmbeddedScroll
                    transactionId={selectedItem.transactionId}
                    initialAnswers={(() => {
                      try {
                        if (selectedItem.questionnaire) {
                          const parsed = JSON.parse(selectedItem.questionnaire);
                          return parsed;
                        }
                      } catch (e) {
                        console.warn("Failed to parse questionnaire JSON", e);
                      }
                      return {};
                    })()}
                    agencyNameOverride={selectedItem.agencyName}
                    agencyPhoneOverride={selectedItem.agencyPhone}
                    agencyEmailOverride={selectedItem.agencyEmail}
                  />
                </div>
              </div>
            ) : (
              <div className="italic text-gray-500">No item selected.</div>
            )}
          </Dialog>
        </div>
      </main>
    </div>
  );
}
